import { Card, Col, Row } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, NumberOutlined, SyncOutlined } from "@ant-design/icons";
import React, { useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import { reducer, initialState } from "./reducer";
import LatestApplications from "./components/LatestApplications";
import PredictionsByMarket from "./components/PredictionsByMarket";
import PredictionsByMarketStatus from "./components/PredictionsByMarketStatus";
import PredictionsByTimeseries from "./components/PredictionsByTimeseries";
import TripleRightLastWeekBar from "./components/TriplePredictions";
import BalanceByTimeseries from "./components/BalanceByTimeSeries";
import PredictionsMarketOptionsColumns from "./components/PredictionsByMarketOptions";
import DashboardServiceInstance from "../../../services/DashboardService";
import { Link, useLocation } from "react-router-dom";
import Layout from "../../../components/layout/Layout";

import {
    FiGrid,
    FiTarget,
    FiBell,
    FiUser,
    FiCreditCard,
    FiLayers,
    FiShield,
} from "react-icons/fi";



const DashboardStatistics = () => {
    const token = useSelector((state) => state.Auth.token);
    const markets = useSelector((state) => state.Utility.markets);
    const [state, dispatch] = useReducer(reducer, initialState);


    const ui = {
        heroWrap: {
            position: "relative",
            padding: "80px 16px 80px",
            background:
                "radial-gradient(1400px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0c1428 100%)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,.06)",
        },
        heroInner: { maxWidth: 1400, margin: "0 auto" },
        badge: {
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
            border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
        },
        heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" },
        heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
    };


    useEffect(() => {
        const fetchPredictionsOverview = async () => {
            try {
                updateLoading(true);
                const response = await DashboardServiceInstance.getPredictionsReportOverview(token);
                const responseData = response.data;
                const { success, result } = responseData;
                const items = success ? result : null;
                dispatch({ type: 'set_predictions_overview', payload: items });
            } catch (error) {
                dispatch({ type: 'set_predictions_overview', payload: null });
            } finally {
                updateLoading(false);
            }
        };

        fetchPredictionsOverview();
    }, [token]);


    useEffect(() => {
        const fetchPredictionsByMarket = async () => {
            try {
                updateLoading(true);
                const response = await DashboardServiceInstance.getPredictionsReportByMarket(token);
                const responseData = response.data;
                const { success, result } = responseData;
                const items = success ? result : null;
                dispatch({ type: 'set_predictions_by_market', payload: items });
            } catch (error) {
                dispatch({ type: 'set_predictions_by_market', payload: null });
            } finally {
                updateLoading(false);
            }
        };

        fetchPredictionsByMarket();
    }, [token]);


    useEffect(() => {
        const fetchPredictionsByMarketOptions = async () => {
            try {
                updateLoading(true);
                const response = await DashboardServiceInstance.getPredictionsReportByMarketOptions(token);
                const responseData = response.data;
                const { success, result } = responseData;
                const items = success ? result?.data : null;
                dispatch({ type: 'set_predictions_by_market_options', payload: items });
            } catch (error) {
                dispatch({ type: 'set_predictions_by_market_options', payload: null });
            } finally {
                updateLoading(false);
            }
        };

        fetchPredictionsByMarketOptions();
    }, [token]);


    useEffect(() => {
        const fetchPredictionsByTimeseries = async () => {
            try {
                updateLoading(true);
                const response = await DashboardServiceInstance.getPredictionsReportByTimeSeries(token);
                const responseData = response.data;
                const { success, result } = responseData;
                const items = success ? result : null;
                dispatch({ type: 'set_prediction_timeseries', payload: items });
            } catch (error) {
                dispatch({ type: 'set_prediction_timeseries', payload: null });
            } finally {
                updateLoading(false);
            }
        };

        fetchPredictionsByTimeseries();
    }, [token]);


    useEffect(() => {
        const fetchBalanceByTimeseries = async () => {
            try {
                updateLoading(true);
                const response = await DashboardServiceInstance.getBalanceReportByTimeSeries(token);
                const responseData = response.data;
                const { success, result } = responseData;
                const items = success ? result : null;
                dispatch({ type: 'set_balance', payload: items });
            } catch (error) {
                dispatch({ type: 'set_balance', payload: null });
            } finally {
                updateLoading(false);
            }
        };

        fetchBalanceByTimeseries();
    }, [token]);


    useEffect(() => {
        const fetchTriplePredictions = async () => {
            try {
                updateLoading(true);
                const response = await DashboardServiceInstance.getTriplePredictions(token);
                const responseData = response.data;
                const { success, result } = responseData;
                const items = success ? result : null;
                dispatch({ type: 'set_triple_predictions', payload: items });
            } catch (error) {
                dispatch({ type: 'set_triple_predictions', payload: null });
            } finally {
                updateLoading(false);
            }
        };

        fetchTriplePredictions();
    }, [token]);


    const updateLoading = (isLoading) => {
        const action = {
            type: 'set_loading',
            payload: isLoading,
        };
        dispatch(action);
    };


    return (
        <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="My Predictions">
            <section style={ui.heroWrap} aria-label="Hero">
                <div style={ui.heroInner}>
                    <span style={ui.badge}><FiTarget /> My Predictions</span>
                    <h1 style={ui.heroTitle}>Your model predictions by result</h1>
                    <p style={ui.heroSub}>
                        Read-only record of your markets, confidence, and outcomes. Filter by result and drill into match pages.
                    </p>
                </div>
            </section>

            <Subnav />

            <section className="px-preds">
                <div className="px-wrap px-wrap--onecol">
                    <Row gutter={24}>
                        <Col span={24}>
                            <Row gutter={24}>
                                <Col xl={6} lg={6} md={24} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <Row gutter={24}>
                                        <Col xl={12} lg={12} md={12} sm={12} xs={12} style={{ marginBottom: 20 }}>
                                            <Card loading={state.isLoading}>
                                                <Row gutter={24} className="text-center" align="middle">
                                                    <Col span={24} className="text-center" style={{ textAlign: 'center', justifyContent: 'center', justifyItems: 'center' }}>
                                                        <div className="p-3 w-100" style={{ backgroundColor: '#e6f4ff', borderRadius: '10%' }}>
                                                            <NumberOutlined style={{ fontSize: '35px', color: "#0958d9" }} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24} className="mt-4">
                                                        <h5 className="text-dark font-weight-bold m-0 p-0">
                                                            {state?.counters?.all}
                                                        </h5>
                                                    </Col>
                                                    <Col span={24} className="mt-1">
                                                        <p className="text-muted m-0 p-0">
                                                            All
                                                        </p>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                        <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                                            <Card loading={state.isLoading}>
                                                <Row gutter={24} className="text-center" align="middle">
                                                    <Col span={24} className="text-center" style={{ textAlign: 'center', justifyContent: 'center', justifyItems: 'center' }}>
                                                        <div className="p-3 w-100" style={{ backgroundColor: '#fffbe6', borderRadius: '10%' }}>
                                                            <SyncOutlined style={{ fontSize: '35px', color: "#d48806" }} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24} className="mt-4">
                                                        <h5 className="text-dark font-weight-bold m-0 p-0">
                                                            {state?.counters?.pending}
                                                        </h5>
                                                    </Col>
                                                    <Col span={24} className="mt-1">
                                                        <p className="text-muted m-0 p-0">
                                                            Pending
                                                        </p>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                        <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                                            <Card loading={state.isLoading}>
                                                <Row gutter={24} className="text-center" align="middle">
                                                    <Col span={24} className="text-center" style={{ textAlign: 'center', justifyContent: 'center', justifyItems: 'center' }}>
                                                        <div className="p-3 w-100" style={{ backgroundColor: '#f6ffed', borderRadius: '10%' }}>
                                                            <CheckCircleOutlined style={{ fontSize: '35px', color: "#389e0d" }} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24} className="mt-4">
                                                        <h5 className="text-dark font-weight-bold m-0 p-0">
                                                            {state?.counters?.correct}
                                                        </h5>
                                                    </Col>
                                                    <Col span={24} className="mt-1">
                                                        <p className="text-muted m-0 p-0">
                                                            Correct
                                                        </p>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                        <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                                            <Card loading={state.isLoading}>
                                                <Row gutter={24} className="text-center" align="middle">
                                                    <Col span={24} className="text-center" style={{ textAlign: 'center', justifyContent: 'center', justifyItems: 'center' }}>
                                                        <div className="p-3 w-100" style={{ backgroundColor: '#fff1f0', borderRadius: '10%' }}>
                                                            <CloseCircleOutlined style={{ fontSize: '35px', color: "#cf1322" }} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24} className="mt-4">
                                                        <h5 className="text-dark font-weight-bold m-0 p-0">
                                                            {state?.counters?.incorrect}
                                                        </h5>
                                                    </Col>
                                                    <Col span={24} className="mt-1">
                                                        <p className="text-muted m-0 p-0">
                                                            Incorrect
                                                        </p>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xl={18} lg={18} md={24} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <LatestApplications
                                        items={state.recentPredictions}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xl={16} lg={16} md={24} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <PredictionsByTimeseries
                                        data={state.timeSeries}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                                <Col xl={8} lg={8} md={24} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <PredictionsMarketOptionsColumns
                                        markets={markets?.filter((item) => item?.code !== 'HT_FT')}
                                        payload={state.predictionsByMarketOptions}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <PredictionsByMarket
                                        data={state.predictionsByMarketType}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                                <Col xl={12} lg={12} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <PredictionsByMarketStatus
                                        data={state.predictionsByMarketStatus}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                                <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                                    <TripleRightLastWeekBar
                                        data={state.triplePredictions}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={24} style={{ marginBottom: 20 }}>
                                <Col span={24}>
                                    <BalanceByTimeseries
                                        data={state.balance}
                                        isLoading={state.isLoading}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>

            </section>
            <style>{`
          .px-preds { background:#f7f7f9; padding:30px; }
          .px-wrap { max-width: 1350px; margin: 0 auto; }
          .px-wrap--onecol { display:block; }
          @media (max-width: 520px) { .px-preds { padding:16px; } }

          .px-card {
            border-radius:18px;
            background:#fff;
            border:1px solid rgba(0,0,0,.06);
            box-shadow: 0 10px 30px rgba(0,0,0,.06);
          }

          .px-toolbar {
            display:flex; align-items:center; justify-content:space-between;
            gap:12px; margin-bottom:12px; flex-wrap:wrap;
          }

          .px-item { padding: 0; border: none; }
          .px-item-card {
            position: relative;
            display: grid;
            grid-template-columns: 1fr auto;
            grid-template-areas: "content actions";
            align-items: center;
            gap: 14px;
            padding: 14px;
            border-radius: 14px;
            border: 1px solid rgba(2,6,23,.06);
            background: #fff;
            box-shadow: 0 6px 16px rgba(2,6,23,.05);
            transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
          }
          .px-item-card:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 28px rgba(2,6,23,.08);
            border-color: rgba(2,6,23,.10);
          }
          .px-item-card.is-pending { background: linear-gradient(180deg,#fff,#fafafa); }

          .px-content { grid-area: content; display:flex; flex-direction:column; gap:6px; min-width:0; }
          .px-toprow { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
          .px-titlewrap { display:flex; align-items:center; gap:10px; flex-wrap:wrap; min-width:0; }
          .px-title { font-weight:800; color:#0f172a; font-size:16px; line-height:1.25; overflow-wrap:anywhere; }
          .px-vs { color:#64748b; font-weight:700; margin: 0 4px; }
          .px-tag { border-radius:999px; }

          .px-time { display:flex; align-items:center; gap:8px; white-space:nowrap; }
          .px-time svg { opacity:.7; }

          .px-body { display:grid; gap:6px; grid-template-columns: 1fr 1fr; }
          .px-kv { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
          .px-k { font-weight:700; color:#0f172a; min-width:72px; }
          .px-v { color:#334155; }
          .px-chip { margin-left:8px; font-size:12px; padding:2px 8px; border-radius:999px; background:#f1f5f9; border:1px solid rgba(2,6,23,.08); }

          .px-actions { grid-area: actions; display:flex; align-items:center; gap:8px; }
          .px-status { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-weight:700; font-size:12px; border:1px solid transparent; }
          .px-status svg { margin-top: -1px; }
          .px-status.px-correct   { background: rgba(22,163,74,.10); color:#166534; border-color: rgba(22,163,74,.25); }
          .px-status.px-incorrect { background: rgba(239,68,68,.10); color:#7f1d1d; border-color: rgba(239,68,68,.25); }
          .px-status.px-pending   { background: rgba(100,116,139,.10); color:#334155; border-color: rgba(100,116,139,.25); }

          @media (max-width: 720px) {
            .px-item-card { grid-template-columns: 1fr; grid-template-areas: "content" "actions"; gap: 10px; padding: 12px 10px; }
            .px-body { grid-template-columns: 1fr; }
            .px-actions { justify-content: flex-end; }
          }

          .px-subnav { position: sticky; top: 0; z-index: 20; backdrop-filter: blur(6px); background: rgba(255,255,255,.85); border-bottom: 1px solid rgba(2,6,23,.08); }
          .px-subnav__inner { max-width: 1400px; margin: 0 auto; padding: 10px 16px; display: flex; gap: 8px; align-items: center; overflow-x: auto; }
          .px-subnav__link { display: inline-flex; align-items: center; padding: 8px 12px; gap: 8px; border-radius: 999px; font-size: 13px; font-weight: 700; color: #334155; text-decoration:none; border: 1px solid transparent; white-space: nowrap; }
          .px-subnav__link:hover { background: #f1f5f9; border-color: rgba(2,6,23,.08); }
          .px-subnav__link.is-active { color: #1e3a8a; background: rgba(37,99,235,.10); border-color: rgba(37,99,235,.25); }
        `}</style>

        </Layout>
    )
};

function Subnav() {
    const LINKS = [
        { to: "/dashboard", label: "Dashboard", Icon: FiGrid },
        { to: "/my/predictions", label: "My predictions", Icon: FiTarget },
        { to: "/notifications", label: "Notifications", Icon: FiBell },
        { to: "/account/profile", label: "My profile", Icon: FiUser },
        { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
        { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
        { to: "/account/security", label: "Change password", Icon: FiShield },
    ];
    const { pathname } = useLocation();
    const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

    return (
        <nav className="px-subnav" aria-label="Account navigation">
            <div className="px-subnav__inner">
                {LINKS.map(({ to, label, Icon }) => (
                    <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
                        <Icon aria-hidden />
                        <span>{label}</span>
                    </Link>
                ))}
            </div>
        </nav>
    );
}

export default DashboardStatistics;